import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { CalendarCheck, Phone, Mail, Check, Clock, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(() => {
    return api.get("/admin/bookings").then((r) => {
      setBookings(r.data.bookings);
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleHandled = async (b) => {
    await api.patch(`/admin/bookings/${b.id}`, { handled: !b.handled });
    toast.success(!b.handled ? "Demande marquée comme traitée" : "Demande remise à traiter");
    load();
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    await api.delete(`/admin/bookings/${pendingDelete.id}`);
    toast.success("Demande supprimée");
    setPendingDelete(null);
    load();
  };

  const stats = {
    total: bookings.length,
    todo: bookings.filter((b) => !b.handled).length,
    done: bookings.filter((b) => b.handled).length,
  };

  let body;
  if (loading) {
    body = <tr><td colSpan={6} className="px-4 py-10 text-center text-[#9aa6c0]">Chargement…</td></tr>;
  } else if (bookings.length === 0) {
    body = <tr><td colSpan={6} className="px-4 py-10 text-center text-[#9aa6c0]">Aucune demande de bilan pour le moment.</td></tr>;
  } else {
    body = bookings.map((b) => (
      <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-[#eef1f6] hover:bg-[#f8f9fc]">
        <td className="px-4 py-3 text-[#4b5b7c] whitespace-nowrap">{fmtDate(b.created_at)}</td>
        <td className="px-4 py-3 font-semibold text-[#1a2d56]">{b.first_name} {b.last_name}</td>
        <td className="px-4 py-3 whitespace-nowrap">
          <a href={`tel:${(b.phone || "").replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 text-[#1a2d56] hover:underline">
            <Phone size={14} className="text-[#9aa6c0]" /> {b.phone}
          </a>
        </td>
        <td className="px-4 py-3">
          <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1.5 text-[#4b5b7c] hover:text-[#1a2d56]">
            <Mail size={14} className="text-[#9aa6c0]" /> {b.email}
          </a>
        </td>
        <td className="px-4 py-3 text-[#4b5b7c] whitespace-nowrap">{b.source}</td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center gap-2 justify-end">
            <button
              data-testid={`booking-toggle-${b.id}`}
              onClick={() => toggleHandled(b)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                b.handled ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-amber-100 text-amber-700 hover:bg-amber-200"
              }`}
            >
              {b.handled ? <><Check size={13} /> Traité</> : <><Clock size={13} /> À traiter</>}
            </button>
            <button data-testid={`booking-delete-${b.id}`} onClick={() => setPendingDelete(b)}
              title="Supprimer" className="text-[#9aa6c0] hover:text-rose-600 p-1">
              <Trash2 size={16} />
            </button>
          </div>
        </td>
      </tr>
    ));
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="font-head text-2xl font-bold text-[#1a2d56] flex items-center gap-2">
          <CalendarCheck size={22} className="text-[#1a2d56]" /> Demandes de bilan
        </h1>
        <p className="text-[#4b5b7c] text-sm mt-1">Les personnes qui souhaitent réserver leur bilan — rappelez-les pour fixer un créneau.</p>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard icon={CalendarCheck} label="Total demandes" value={stats.total} />
        <StatCard icon={Clock} label="À traiter" value={stats.todo} accent />
        <StatCard icon={Check} label="Traitées" value={stats.done} />
      </div>

      <div className="bg-white rounded-2xl border border-[#e2e8f0] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bookings-table">
            <thead className="bg-[#f8f9fc] text-[#4b5b7c] text-left">
              <tr>
                {["Date", "Nom", "Téléphone", "Email", "Source", ""].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>{body}</tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="booking-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette demande ?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete ? `${pendingDelete.first_name} ${pendingDelete.last_name || ""} — ${pendingDelete.phone}. ` : ""}
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="booking-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="booking-delete-confirm"
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#e2e8f0] p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent ? "bg-[#d7de3b] text-[#1a2d56]" : "bg-[#f0f2f8] text-[#1a2d56]"}`}>
        <Icon size={20} />
      </div>
      <div>
        <div className="text-2xl font-head font-extrabold text-[#1a2d56]">{value}</div>
        <div className="text-xs text-[#4b5b7c]">{label}</div>
      </div>
    </div>
  );
}
